import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy, KeyRound, Link2, Plug, Plus, RotateCw, UserPlus, Webhook } from "lucide-react";
import { defineModuleUi, from, rpc, unwrap, useAction, useHasRole, useOrg, useSession } from "@vixio/data";
import {
  Button, Callout, Checkbox, ConfirmDialog, DataTable, DateInput, Dialog, NumberInput, PageHeader, Panel, Select, StatusFromMap, StatusPill, Tabs, TextInput,
  formatDateTime, formatInt, formatRelative, useToast, zonedIso,
} from "@vixio/ui";
import "./api";
import { API_RESOURCES, CONN_STATUS, DELIVERY_STATUS, HEALTH, SCHEME, type ApiKey, type ConnectionView, type Delivery, type Endpoint, type Provider } from "./api";
import { AiTab, BillingTab, EmailTab, FiscalTab, GoogleSecretPanel, HubTab, VaultTab } from "./central";
import { ConnectionTestButton } from "./secrets";

const SCOPE_LABEL: Record<string, string> = { "calendar.events": "eventos da agenda", "calendar.freebusy": "livre ou ocupado", "calendar.readonly": "leitura da agenda", "drive.file": "arquivos do Drive criados pelo Vixio" };
const scopeLabel = (s: string) => {
  const short = s.replace("https://www.googleapis.com/auth/", "");
  return SCOPE_LABEL[short] ?? short;
};

export * from "./api";
export { ConnectionTestButton, SecretField, SecretStatus, randomSecret, useSystemSecrets, type SecretFieldProps } from "./secrets";

function IntegrationsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("aba") ?? "email";
  return (
    <div className="vx-content">
      <PageHeader
        title="Integrações"
        description="Central de Integrações: e-mail, cobrança, NFS-e, WhatsApp, IA e Google configurados aqui, com os segredos cifrados no cofre do painel e o botão Testar conexão. Nada fica no servidor além das chaves-mestras."
      />
      <Tabs
        label="Integrações"
        value={tab}
        onValueChange={(v) => setParams({ aba: v }, { replace: true })}
        tabs={[
          { value: "email", label: "E-mail", content: <EmailTab /> },
          { value: "cobranca", label: "Cobrança", content: <BillingTab /> },
          { value: "nfse", label: "NFS-e", content: <FiscalTab /> },
          { value: "whatsapp", label: "WhatsApp", content: <HubTab /> },
          { value: "ia", label: "IA", content: <AiTab /> },
          { value: "conexoes", label: "Google", content: <ConnectionsTab /> },
          { value: "cofre", label: "Cofre", content: <VaultTab /> },
          { value: "chaves", label: "Chaves de API", content: <ApiKeysTab /> },
          { value: "webhooks", label: "Webhooks de entrada", content: <EndpointsTab /> },
        ]}
      />
    </div>
  );
}

/** Mostra um segredo uma única vez, com cópia. */
function OnceSecret({ label, value, extra }: { label: string; value: string; extra?: string }) {
  const toast = useToast();
  return (
    <div className="vx-stack">
      <Callout tone="warn" title="Copie agora">Este valor aparece só esta vez. Depois, só dá para revogar ou girar.</Callout>
      <div className="vx-field">
        <span className="vx-field__label">{label}</span>
        <div className="vx-copy">
          <input className="vx-input" readOnly value={value} aria-label={label} data-testid="once-secret" onFocus={(e) => e.currentTarget.select()} />
          <Button
            icon={<Copy />}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(value);
                toast.ok("Copiado");
              } catch {
                toast.error("Não foi possível copiar", "Selecione e copie manualmente.");
              }
            }}
          >
            Copiar
          </Button>
        </div>
      </div>
      {extra ? <p className="text-2">{extra}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------- conexões

function ConnectionsTab() {
  const org = useOrg();
  const session = useSession();
  const toast = useToast();
  const isAdmin = useHasRole("admin");
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ConnectionView | null>(null);
  const conns = useQuery({ queryKey: ["integrations", "connections", org.id], queryFn: () => unwrap<ConnectionView[]>(from("integrations", "v_connections").select("*").eq("org_id", org.id).order("created_at")) });
  const clientId = useQuery({ queryKey: ["integrations", "google_client_id", org.id], queryFn: async () => ((await rpc("core", "get_setting", { p_org: org.id, p_key: "integrations.google_client_id" })) as string | null) ?? "" });
  const revoke = useAction((c: ConnectionView) => rpc("integrations", "revoke_connection", { p_connection: c.id }), { invalidate: [["integrations"]], success: "Conexão revogada; tokens apagados" });
  // O painel só inicia: o retorno (troca do código e guarda cifrada dos tokens) é no worker, no endereço do servidor.
  const start = useAction((c: ConnectionView) => rpc("integrations", "start_google_oauth", { p_connection: c.id }), {
    onSuccess: (r) => window.location.assign(r.auth_url),
  });
  const connectMine = useAction(() => rpc("integrations", "connect_my_google", { p_org: org.id }), {
    onSuccess: (r) => window.location.assign(r.auth_url),
  });
  // Volta do Google: ?google=ok|erro&motivo=…
  const shown = useRef(false);
  useEffect(() => {
    const g = params.get("google");
    if (!g || shown.current) return;
    shown.current = true;
    if (g === "ok") toast.ok("Conta Google conectada");
    else toast.error("Google não conectado", params.get("motivo") ?? "Tente de novo.");
    const next = new URLSearchParams(params);
    next.delete("google");
    next.delete("motivo");
    setParams(next, { replace: true });
  }, [params, setParams, toast]);
  const me = session.userId ?? null;
  return (
    <div className="vx-stack vx-stack--lg">
      {/* Ações fora da tabela: em modo "bare" o DataTable não mostra a barra de ferramentas. */}
      <div className="vx-row vx-row--end" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button size="sm" icon={<UserPlus />} onClick={() => connectMine.mutate(undefined)} loading={connectMine.isPending}>Conectar minha conta Google</Button>
        {isAdmin ? <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>Nova conexão</Button> : null}
      </div>
      <DataTable<ConnectionView>
        caption="Conexões"
        data={conns.data ?? []}
        loading={conns.isLoading}
        rowKey={(c) => c.id}
        bare
        empty="Nenhuma conexão. Conecte a sua conta Google para a sua agenda, ou crie a conexão da equipe."
        columns={[
          { key: "label", header: "Conexão", cell: (c) => <span className="vx-table__primary">{c.label}<span className="vx-table__sub">{c.provider_label}{c.owner_user ? " · pessoal" : " · da organização"}{c.account_email ? ` · ${c.account_email}` : ""}</span></span> },
          { key: "scopes", header: "Escopos", cell: (c) => <span className="vx-break" style={{ fontSize: "var(--fs-12)" }}>{c.scopes.filter((x) => x !== "openid" && x !== "email").map(scopeLabel).join(", ") || "-"}</span> },
          { key: "status", header: "Situação", cell: (c) => <StatusFromMap map={CONN_STATUS} value={c.status} /> },
          { key: "health", header: "Saúde", cell: (c) => <span className="vx-table__primary"><StatusFromMap map={HEALTH} value={c.health} />{c.last_error ? <span className="vx-table__sub text-crit vx-break">{c.last_error}</span> : null}</span> },
          { key: "last_ok_at", header: "Último uso bem-sucedido", cell: (c) => (c.last_ok_at ? <span className="num">{formatDateTime(c.last_ok_at)}</span> : <span className="muted">-</span>) },
          {
            key: "actions",
            header: <span className="sr-only">Ações</span>,
            align: "right",
            cell: (c) => {
              const mine = Boolean(c.owner_user && c.owner_user === me);
              if (c.status === "revogada" || !(isAdmin || mine)) return null;
              return (
                <span className="vx-row vx-row--end" style={{ flexWrap: "wrap", gap: 6 }}>
                  {c.provider.startsWith("google") ? (
                    <Button size="sm" icon={<Link2 />} onClick={() => start.mutate(c)} loading={start.isPending}>{c.status === "ativa" ? "Reconectar" : "Conectar com Google"}</Button>
                  ) : null}
                  {isAdmin && c.provider === "google_calendar" && c.status !== "pendente" ? (
                    <ConnectionTestButton provider="google_calendar" params={{ connection_id: c.id }} />
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => setRevoking(c)}>Revogar</Button>
                </span>
              );
            },
          },
        ]}
      />
      {isAdmin ? <GoogleClientPanel current={clientId.data ?? ""} /> : null}
      <Callout tone="info">
        A saúde vem de cada uso: renovação do token (antes de vencer), sincronia da agenda e Testar conexão. 3 falhas seguidas marcam erro
        e avisam. Para usar o Google Agenda numa agenda de horários, ligue a sincronia em Relacionamento → Horários.
      </Callout>
      <ConnectionDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog open={revoking !== null} onOpenChange={(o) => !o && setRevoking(null)} title={`Revogar ${revoking?.label ?? ""}`} confirmLabel="Revogar" destructive confirmText="REVOGAR" onConfirm={() => revoke.mutateAsync(revoking!)}>
        <p>Os tokens guardados são apagados. Para voltar a usar, crie a conexão de novo e autorize.</p>
      </ConfirmDialog>
    </div>
  );
}

function GoogleClientPanel({ current }: { current: string }) {
  const org = useOrg();
  const toast = useToast();
  const workerUrl = useQuery({ queryKey: ["integrations", "worker_public_url", org.id], queryFn: async () => ((await rpc("core", "get_setting", { p_org: org.id, p_key: "worker.public_url" })) as string | null) ?? "" });
  const redirect = workerUrl.data ? `${workerUrl.data.replace(/[/]+$/, "")}/hooks/oauth/google` : "";
  const [v, setV] = useState(current);
  useEffect(() => setV(current), [current]);
  const save = useAction(() => rpc("core", "set_setting", { p_org: org.id, p_key: "integrations.google_client_id", p_value: v.trim() }), { invalidate: [["integrations"]], success: "Client ID salvo" });
  return (
    <Panel title="Aplicativo Google (OAuth)">
      <div className="vx-row" style={{ alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <TextInput label="Client ID" value={v} onChange={setV} />
        </div>
        <Button onClick={() => save.mutate(undefined)} loading={save.isPending} disabled={!v.trim() || v.trim() === current}>Salvar</Button>
      </div>
      <GoogleSecretPanel />
      <div className="vx-field" style={{ marginTop: 12 }}>
        <span className="vx-field__label">Endereço de retorno (cadastre no app do Google, em "URIs de redirecionamento autorizados")</span>
        {redirect ? (
          <div className="vx-copy">
            <input className="vx-input mono" readOnly value={redirect} aria-label="Endereço de retorno do OAuth" data-testid="google-redirect-uri" onFocus={(e) => e.currentTarget.select()} />
            <Button icon={<Copy />} onClick={async () => { await navigator.clipboard?.writeText(redirect); toast.ok("Copiado"); }}>Copiar</Button>
          </div>
        ) : (
          <p className="text-crit" style={{ fontSize: "var(--fs-12)" }}>Configure o endereço público do worker (worker.public_url) em Configurações → Gerais.</p>
        )}
      </div>
    </Panel>
  );
}

function ConnectionDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const providers = useQuery({ queryKey: ["integrations", "providers"], queryFn: () => unwrap<Provider[]>(from("integrations", "providers").select("*").order("label")) });
  const [provider, setProvider] = useState("google_calendar");
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (open) {
      setProvider("google_calendar");
      setLabel("");
    }
  }, [open]);
  const p = (providers.data ?? []).find((x) => x.key === provider);
  const save = useAction(() => rpc("integrations", "create_connection", { p_org: org.id, p_provider: provider, p_label: label.trim() }), {
    invalidate: [["integrations"]],
    success: "Conexão criada; autorize para ativar",
    onSuccess: () => onOpenChange(false),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Nova conexão" onSubmit={() => label.trim().length >= 2 && save.mutate(undefined)} footer={<><Button onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" variant="primary" loading={save.isPending} disabled={label.trim().length < 2}>Criar</Button></>}>
      <div className="vx-stack">
        <Select label="Serviço" value={provider} onChange={setProvider} options={(providers.data ?? []).filter((x) => x.auth_kind === "oauth2").map((x) => ({ value: x.key, label: x.label }))} />
        <TextInput label="Nome" value={label} onChange={setLabel} required maxLength={120} hint="Ex.: Agenda do Stênio" />
        {p ? <p className="text-2" style={{ fontSize: "var(--fs-12)" }}>Escopos: {p.default_scopes.map(scopeLabel).join(", ")}</p> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- chaves de API

function ApiKeysTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const keys = useQuery({ queryKey: ["integrations", "api_keys", org.id], enabled: isAdmin, queryFn: () => unwrap<ApiKey[]>(from("integrations", "api_keys").select("id, name, prefix, scopes, rate_limit_per_minute, status, expires_at, last_used_at, revoked_at, created_at").eq("org_id", org.id).order("created_at", { ascending: false })) });
  const revoke = useAction((k: ApiKey) => rpc("integrations", "revoke_api_key", { p_key: k.id }), { invalidate: [["integrations"]], success: "Chave revogada" });
  if (!isAdmin) return <Callout tone="info">Só administradores veem e criam chaves de API.</Callout>;
  return (
    <>
      <DataTable<ApiKey>
        caption="Chaves de API"
        data={keys.data ?? []}
        loading={keys.isLoading}
        rowKey={(k) => k.id}
        bare
        toolbar={<Button size="sm" variant="primary" icon={<KeyRound />} onClick={() => setCreating(true)}>Nova chave</Button>}
        empty="Nenhuma chave."
        columns={[
          { key: "name", header: "Chave", cell: (k) => <span className="vx-table__primary">{k.name}<span className="vx-table__sub mono">{k.prefix}…</span></span> },
          { key: "scopes", header: "Escopos", cell: (k) => <span className="mono vx-break" style={{ fontSize: "var(--fs-12)" }}>{k.scopes.join(", ")}</span> },
          { key: "rate_limit_per_minute", header: "Limite", align: "right", cell: (k) => <span className="num">{formatInt(k.rate_limit_per_minute)}/min</span> },
          { key: "last_used_at", header: "Último uso", cell: (k) => (k.last_used_at ? <span className="num">{formatRelative(k.last_used_at)}</span> : <span className="muted">nunca</span>) },
          { key: "expires_at", header: "Vence", kind: "date" },
          { key: "status", header: "Situação", cell: (k) => (k.status === "ativa" ? <StatusPill tone="ok">ativa</StatusPill> : <StatusPill>revogada</StatusPill>) },
          { key: "actions", header: <span className="sr-only">Ações</span>, align: "right", cell: (k) => (k.status === "ativa" ? <Button size="sm" variant="ghost" onClick={() => setRevoking(k)}>Revogar</Button> : null) },
        ]}
      />
      <ApiKeyDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog open={revoking !== null} onOpenChange={(o) => !o && setRevoking(null)} title={`Revogar ${revoking?.name ?? ""}`} confirmLabel="Revogar" destructive confirmText="REVOGAR" onConfirm={() => revoke.mutateAsync(revoking!)}>
        <p>Chamadas com esta chave passam a ser recusadas na hora. Não dá para desfazer; crie outra chave se precisar.</p>
      </ConfirmDialog>
    </>
  );
}

function ApiKeyDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [limit, setLimit] = useState<number | null>(60);
  const [expires, setExpires] = useState("");
  const [created, setCreated] = useState<{ key: string; prefix: string } | null>(null);
  useEffect(() => {
    if (open) {
      setName("");
      setScopes([]);
      setLimit(60);
      setExpires("");
      setCreated(null);
    }
  }, [open]);
  const save = useAction(() => rpc("integrations", "create_api_key", { p_org: org.id, p_name: name.trim(), p_scopes: scopes, p_rate_limit_per_minute: limit ?? 60, p_expires_at: expires ? zonedIso(expires, "23:59") : null }), {
    invalidate: [["integrations"]],
    success: "Chave criada",
    onSuccess: (r) => setCreated({ key: r.key, prefix: r.prefix }),
  });
  const toggle = (s: string, on: boolean) => setScopes((x) => (on ? [...x, s] : x.filter((y) => y !== s)));
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={created ? "Chave criada" : "Nova chave de API"}
      onSubmit={created ? () => onOpenChange(false) : () => name.trim().length >= 2 && scopes.length > 0 && save.mutate(undefined)}
      footer={created ? <Button type="submit" variant="primary">Já copiei</Button> : <><Button onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" variant="primary" loading={save.isPending} disabled={name.trim().length < 2 || scopes.length === 0}>Criar chave</Button></>}
    >
      {created ? (
        <OnceSecret label="Chave de API" value={created.key} extra={`O banco guarda só o hash; na lista a chave aparece como ${created.prefix}….`} />
      ) : (
        <div className="vx-stack">
          <TextInput label="Nome" value={name} onChange={setName} required maxLength={120} hint="Quem vai usar, ex.: Planilha do Sebrae" />
          <fieldset className="vx-fieldset">
            <legend>Escopos</legend>
            <div className="vx-stack" style={{ gap: 4 }}>
              {API_RESOURCES.map((r) => (
                <div key={r.key} className="vx-row" style={{ gap: 16 }}>
                  <span style={{ width: 120 }}>{r.label}</span>
                  <Checkbox label="ler" checked={scopes.includes(`${r.key}:read`)} onChange={(v) => toggle(`${r.key}:read`, v)} />
                  <Checkbox label="gravar" checked={scopes.includes(`${r.key}:write`)} onChange={(v) => toggle(`${r.key}:write`, v)} />
                </div>
              ))}
            </div>
          </fieldset>
          <div className="vx-form-grid">
            <NumberInput label="Limite por minuto" value={limit} onChange={setLimit} min={1} max={6000} />
            <DateInput label="Vence em" value={expires} onChange={setExpires} hint="Opcional." />
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- webhooks de entrada

function EndpointsTab() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string; extra?: string } | null>(null);
  const [selected, setSelected] = useState<Endpoint | null>(null);
  const eps = useQuery({ queryKey: ["integrations", "endpoints", org.id], queryFn: () => unwrap<Endpoint[]>(from("integrations", "inbound_endpoints").select("*").eq("org_id", org.id).order("created_at")) });
  const deliveries = useQuery({
    queryKey: ["integrations", "deliveries", selected?.id],
    enabled: isAdmin && selected !== null,
    queryFn: () => unwrap<Delivery[]>(from("integrations", "inbound_deliveries").select("*").eq("endpoint_id", selected!.id).order("created_at", { ascending: false }).limit(200)),
  });
  const rotate = useAction((e: Endpoint) => rpc("integrations", "rotate_endpoint_secret", { p_endpoint: e.id }), {
    invalidate: [["integrations"]],
    success: "Segredo girado",
    onSuccess: (v) => setSecret({ title: "Novo segredo", value: v, extra: "Atualize o remetente: o segredo anterior deixa de valer agora." }),
  });
  const setStatus = useAction((a: { e: Endpoint; s: "ativo" | "desativado" }) => rpc("integrations", "set_endpoint_status", { p_endpoint: a.e.id, p_status: a.s }), { invalidate: [["integrations"]], success: "Situação atualizada" });
  return (
    <div className="vx-stack vx-stack--lg">
      <DataTable<Endpoint>
        caption="Webhooks de entrada"
        data={eps.data ?? []}
        loading={eps.isLoading}
        rowKey={(e) => e.id}
        bare
        onRowClick={isAdmin ? setSelected : undefined}
        toolbar={isAdmin ? <Button size="sm" variant="primary" icon={<Webhook />} onClick={() => setCreating(true)}>Novo webhook</Button> : null}
        empty="Nenhum webhook de entrada."
        columns={[
          { key: "label", header: "Webhook", cell: (e) => <span className="vx-table__primary">{e.label}<span className="vx-table__sub mono">/hooks/in/{e.id}</span></span> },
          { key: "signature_scheme", header: "Assinatura", cell: (e) => <span className="vx-table__primary">{e.signature_scheme === "vixio_v1" ? "Vixio v1" : "HMAC hex"}<span className="vx-table__sub mono">{e.signature_header}</span></span> },
          { key: "provider", header: "Origem" },
          { key: "last_received_at", header: "Último recebido", cell: (e) => (e.last_received_at ? <span className="num">{formatDateTime(e.last_received_at)}</span> : <span className="muted">nunca</span>) },
          { key: "status", header: "Situação", cell: (e) => (e.status === "ativo" ? <StatusPill tone="ok">ativo</StatusPill> : <StatusPill>desativado</StatusPill>) },
          {
            key: "actions",
            header: <span className="sr-only">Ações</span>,
            align: "right",
            cell: (e) =>
              isAdmin ? (
                <span className="vx-row vx-row--end" style={{ flexWrap: "nowrap" }} onClick={(ev) => ev.stopPropagation()}>
                  <Button size="sm" icon={<RotateCw />} onClick={() => rotate.mutate(e)}>Girar segredo</Button>
                  <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ e, s: e.status === "ativo" ? "desativado" : "ativo" })}>{e.status === "ativo" ? "Desativar" : "Ativar"}</Button>
                </span>
              ) : null,
          },
        ]}
      />
      {selected ? (
        <DataTable<Delivery>
          caption={`Entregas de ${selected.label}`}
          data={deliveries.data ?? []}
          loading={deliveries.isLoading}
          rowKey={(d) => d.id}
          filters={[{ key: "status", label: "Situação", options: Object.entries(DELIVERY_STATUS).map(([value, s]) => ({ value, label: s.label })) }]}
          empty="Nenhuma tentativa recebida ainda."
          toolbar={<span className="text-2">Entregas de {selected.label}</span>}
          columns={[
            { key: "created_at", header: "Recebido", kind: "datetime" },
            { key: "status", header: "Resultado", cell: (d) => <StatusFromMap map={DELIVERY_STATUS} value={d.status} /> },
            { key: "size_bytes", header: "Tamanho", align: "right", cell: (d) => <span className="num">{formatInt(d.size_bytes)} B</span> },
            { key: "body_sha256", header: "Hash do corpo", cell: (d) => <span className="vx-id">{d.body_sha256.slice(0, 16)}</span> },
            { key: "event_id", header: "Evento", cell: (d) => (d.event_id ? <span className="num">#{d.event_id}</span> : <span className="muted">-</span>) },
          ]}
        />
      ) : isAdmin ? (
        <p className="muted">Escolha um webhook para ver as tentativas recebidas.</p>
      ) : null}
      <Callout tone="info">Cada entrega aceita vira o evento integrations.inbound_received, que pode disparar uma automação (filtre por payload.endpoint_key).</Callout>
      <EndpointDialog open={creating} onOpenChange={setCreating} onCreated={(r) => setSecret({ title: "Segredo do webhook", value: r.secret, extra: `Envie para ${r.path} no domínio do worker, assinado com este segredo.` })} />
      <Dialog open={secret !== null} onOpenChange={(o) => !o && setSecret(null)} title={secret?.title ?? ""} onSubmit={() => setSecret(null)} footer={<Button type="submit" variant="primary">Já copiei</Button>}>
        {secret ? <OnceSecret label="Segredo" value={secret.value} extra={secret.extra} /> : null}
      </Dialog>
    </div>
  );
}

function EndpointDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (r: { path: string; secret: string }) => void }) {
  const org = useOrg();
  const [f, setF] = useState({ key: "", label: "", scheme: "vixio_v1", header: "", provider: "webhook_generico" });
  useEffect(() => {
    if (open) setF({ key: "", label: "", scheme: "vixio_v1", header: "", provider: "webhook_generico" });
  }, [open]);
  const ok = /^[a-z0-9][a-z0-9-]{1,62}$/.test(f.key) && f.label.trim().length >= 2 && (!f.header || /^[a-z0-9-]{1,64}$/.test(f.header));
  const save = useAction(() => rpc("integrations", "create_inbound_endpoint", { p_org: org.id, p_key: f.key, p_label: f.label.trim(), p_signature_scheme: f.scheme, p_signature_header: f.header || null, p_provider: f.provider }), {
    invalidate: [["integrations"]],
    success: "Webhook criado",
    onSuccess: (r) => {
      onOpenChange(false);
      onCreated(r);
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Novo webhook de entrada" onSubmit={() => ok && save.mutate(undefined)} footer={<><Button onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" variant="primary" loading={save.isPending} disabled={!ok}>Criar</Button></>}>
      <div className="vx-form-grid">
        <TextInput label="Nome" value={f.label} onChange={(v) => setF({ ...f, label: v })} required maxLength={120} />
        <TextInput label="Chave" value={f.key} onChange={(v) => setF({ ...f, key: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className="mono" required hint="Vai no evento como endpoint_key." />
        <Select className="span-2" label="Assinatura" value={f.scheme} onChange={(v) => setF({ ...f, scheme: v })} options={Object.entries(SCHEME).map(([value, label]) => ({ value, label }))} />
        <TextInput label="Cabeçalho da assinatura" value={f.header} onChange={(v) => setF({ ...f, header: v.toLowerCase() })} className="mono" hint="Vazio: o padrão do esquema." />
        <Select label="Origem" value={f.provider} onChange={(v) => setF({ ...f, provider: v })} options={[{ value: "webhook_generico", label: "Webhook genérico" }, { value: "n8n", label: "n8n" }]} />
      </div>
    </Dialog>
  );
}

export default defineModuleUi({
  key: "integrations",
  title: "Integrações",
  domain: "platform",
  icon: Plug,
  menu: [{ path: "/configuracoes/integracoes", label: "Integrações", minRole: "member", icon: Plug, order: 60, keywords: "google agenda drive oauth chave api webhook n8n smtp e-mail asaas cobrança nfs-e whatsapp openrouter ia segredo cofre senha testar conexão" }],
  routes: [{ path: "/configuracoes/integracoes", element: <IntegrationsPage />, minRole: "member" }],
});
